import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addVehicle } from "../../../actions/admin/vehicle";
import { getVehicleTypes } from "../../../actions/admin/vehicletype";
import Alert from "../../layout/Alert";
import AdminPage from "../_components/AdminPage";
import {
  AdminFormCard,
  AdminTextField,
  AdminSelectField,
  AdminToggleField,
  AdminBackLink,
  AdminFormButton,
  AdminFormActions,
} from "../_components/AdminForm";
import ConfirmationModal from "../components/ConfirmationModal";
import SuccessModal from "../components/SuccessModal";
import { ValueList, summarizeFields, asText, asOnOff } from "../components/ChangeSummary";
import useSaveConfirmation from "../components/useSaveConfirmation";

const VEHICLE_LIST = "/admin/vehiclelist";

// The fields this form writes, in the order it shows them — the confirmation dialog
// lists what is about to be created from this, so it reads in the same order it was
// filled in.
export const VEHICLE_FIELDS = [
  { name: "vehiclename", label: "Vehicle name", format: asText },
  { name: "vehicletype", label: "Vehicle type", format: asText },
  { name: "registration", label: "Vehicle registration", format: asText },
  { name: "isactive", label: "Active", format: asOnOff },
];

const AddVehicle = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { vehicletypes } = useSelector((state) => state.vehicletype);

  const vehicleTypeOptions = useMemo(
    () => (vehicletypes || []).map((d) => ({ value: d._id, label: d.typename })),
    [vehicletypes]
  );

  const [formData, setFormData] = useState({
    vehiclename: "",
    vehicletype: null,
    vehicletypeid: null,
    registration: "",
    isactive: false,
  });
  const { vehiclename, vehicletype, registration, isactive } = formData;

  useEffect(() => {
    dispatch(getVehicleTypes());
  }, [dispatch]);

  const save = useSaveConfirmation({
    save: () => dispatch(addVehicle(formData, null)),
    onDismiss: () => history.push(VEHICLE_LIST),
  });

  // Submitting no longer writes anything: it summarises what would be created and asks.
  const onSubmit = (e) => {
    e.preventDefault();
    save.request(summarizeFields(VEHICLE_FIELDS, formData));
  };

  // Keep both the label (shown in the form/summary) and the id (what the backend links on).
  const setVehicleTypeOption = (option) => {
    setFormData({ ...formData, vehicletype: option.label, vehicletypeid: option.value });
  };

  return (
    <AdminPage title="Add Vehicle" actions={<AdminBackLink to={VEHICLE_LIST} />}>
      <form onSubmit={onSubmit} className="tw:max-w-[720px]">
        <AdminFormCard title="Vehicle status">
          <AdminToggleField
            name="isactive"
            label="Vehicle is active"
            description="Only active vehicles can be assigned to a booking."
            checked={isactive}
            onChange={() => setFormData({ ...formData, isactive: !isactive })}
          />
        </AdminFormCard>

        <AdminFormCard title="Vehicle details" columns={2}>
          <AdminTextField
            name="vehiclename"
            label="Vehicle name"
            placeholder="Enter name"
            value={vehiclename}
            onChange={(e) => setFormData({ ...formData, vehiclename: e.target.value })}
            required
          />

          <AdminSelectField
            name="vehicletype"
            label="Vehicle type"
            placeholder="Select a vehicle type"
            options={vehicleTypeOptions}
            value={vehicleTypeOptions.find((o) => o.label === vehicletype) || null}
            onChange={setVehicleTypeOption}
          />

          <AdminTextField
            name="registration"
            label="Vehicle registration"
            placeholder="Enter vehicle registration"
            value={registration}
            onChange={(e) => setFormData({ ...formData, registration: e.target.value })}
            required
          />
        </AdminFormCard>

        <AdminFormActions>
          <AdminFormButton type="submit">Add Vehicle</AdminFormButton>
        </AdminFormActions>
      </form>

      <Alert />

      <ConfirmationModal
        show={!!save.summary}
        title="Create this vehicle?"
        description="It will be added to the fleet and can be assigned to bookings straight away."
        confirmLabel="Add Vehicle"
        busyLabel="Creating..."
        isBusy={save.isSaving}
        error={save.error}
        onCancel={save.cancel}
        onConfirm={save.confirm}
      >
        <ValueList rows={save.summary || []} />
      </ConfirmationModal>

      <SuccessModal
        show={!!save.saved}
        title="Vehicle created"
        description="It is in the fleet now. Closing this returns you to the vehicle list."
        onClose={save.dismiss}
      >
        <ValueList rows={save.saved || []} />
      </SuccessModal>
    </AdminPage>
  );
};

export default AddVehicle;
